import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { NotificationManager } from 'react-notifications';
import { useNavigate } from 'react-router-dom';

import Saral from '../../images/Saral-bg.png';
import { login } from '../../services/auth.service';
import { getStorage, isAuthenticated, removeStorage, setStorage, validateEmail } from '../../utils/auth-utils';
import ButtonComponent from '../common-components/ButtonComponent/ButtonComponent';
import { VisibilityIcon, VisibilityOffIcon } from '../Icons';

import './styles/login.scss';

import { SWITCH_ID_KEY } from '@/utils/constants';
import { loginSchema } from '@/validations/auth';
import { Checkbox } from '@mui/material';
import { EMAIL_REGEX } from '@Saral-Influencer-Marketing/saral-utils/dist/constants/regex.js';
import * as Sentry from '@sentry/react';
import Gleap from 'gleap';
import Joi from 'joi';
import { useFeatureFlagEnabled } from 'posthog-js/react';

import { ciIdentify } from '../../config/axios-config';
import { passwordRegex, triggerGTMViewPageEvent } from '../../utils/global';
import { Mixpanel } from '../../utils/mixpanel';
import CSDowntimeBanner from '../Dashboard/home/components/CSDowntimeBanner';
import { PrimaryButton } from '../styledComponents';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [disableSubmitButton, setDisableSubmitButton] = useState();
  const [error_message, setErrorMessage] = useState(false);
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();
  const [rememberMe, setRememberMe] = useState(false);

  const Deployed36HoursAgo = useFeatureFlagEnabled('show_hard_refresh_banner');

  useEffect(() => {
    triggerGTMViewPageEvent('Login');
    const rememberedEmail = window.localStorage.getItem('remember-email');

    if (rememberedEmail?.length) {
      setEmail(rememberedEmail);
    }
  }, []);

  const validateData = async (body) => {
    if (password.startsWith(' ')) {
      setErrors({ password: 'It seems you entered an extra space at the start' });
      return false;
    } else if (password.endsWith(' ')) {
      setErrors({ password: 'It seems you entered a trailing space' });
      return false;
    }
    const { error } = loginSchema.validate(body, { abortEarly: false });

    if (error) {
      const validationErrors = {};
      error.details.forEach((detail) => {
        validationErrors[detail.context.key] = detail.message;
      });

      setErrors(validationErrors);
      return false;
    }
    return true;
  };
  const gtmLogic = (data) => {
    Mixpanel.identify(data?.userId);
    Mixpanel.people.set({
      $first_name: data?.first_name,
      $last_name: data?.last_name,
      $distinct_id: data?.userId,
      $email: data?.email,
      $created: data?.created_at,
      'Plan Name': data?.plan?.name,
      'Plan Price': data?.plan?.price,
      'Plan Frequency': data?.plan?.type,
      'Unique User ID': data?.userId,
      'Saral Mastery Level': data?.beltStatus,
    });
    Mixpanel.track('login', {
      planName: data?.plan?.name,
      planPrice: data?.plan?.price,
      planType: data?.plan?.type,
      userId: data?.userId,
    });
  };

  const signInUser = async (e) => {
    e.preventDefault();
    if (disableSubmitButton) {
      return;
    }
    if (isAuthenticated()) {
      window.location.reload();
      return;
    }

    const body = { email, password };
    setDisableSubmitButton(true);
    const isValid = await validateData(body);
    if (!isValid) {
      setDisableSubmitButton(false);
      return;
    }
    const response = await login(body);
    if (response?.status) {
      gtmLogic(response?.data);
      NotificationManager.success('Login Successful!', '', 1000);
      setStorage('token', response.data.token);
      Cookies.set('access_token', response.data.token, { expires: 15 });
      ciIdentify({ id: response?.data?.userId });

      Gleap.identify(`${response?.data?.teamMemberId}`, {
        name: `${response?.data?.first_name} ${response?.data?.last_name}`,
        email: response?.data?.email,
        plan: response?.data?.plan?.name,
      });

      removeStorage(SWITCH_ID_KEY);

      setTimeout(() => {
        window.location.reload();
        setDisableSubmitButton(false);
      }, 1000);

      Sentry.setUser(null);
      if (rememberMe) {
        window.localStorage.setItem('remember-email', email);
      } else {
        window.localStorage.removeItem('remember-email');
      }
    } else {
      if (typeof response?.data === 'string' && response?.data?.includes('Please confirm your email')) {
        setErrorMessage(response.data);
      } else {
        NotificationManager.error(
          typeof response?.data === 'string'
            ? response?.data
            : typeof response?.message === 'string'
              ? response?.message
              : 'Error While Login',
          '',
          1200
        );
      }
      setDisableSubmitButton(false);
    }
  };

  let isLoginDone = getStorage('login');

  let showHardRefresh;

  if (isLoginDone) {
    showHardRefresh = false;
  } else {
    showHardRefresh = true;
  }

  setStorage('login', 'done');

  return (
    <div className="login-page-layout">
      {showHardRefresh && Deployed36HoursAgo && (
        <div className="login-page-banner">
          <div className="login-page-banner-inside">
            <p>Can’t log in? Hard refresh the page with Cmd + Shift + R and try again</p>
          </div>
        </div>
      )}
      <div className="login-main-div">
        <div className="login-form">
          <img src={Saral} alt="" width={160} className="saral-logo" />
          <h2>Get back into your account</h2>
          <form>
            <div className="form-group">
              <label className="required-field">Email</label>
              <input
                type="text"
                placeholder="Enter your email"
                value={email}
                name="email"
                onChange={(e) => {
                  setErrors({});
                  setEmail(e.target.value);
                }}
                style={{ borderColor: errors?.email ? '#e64848' : '#d0d5dd' }}
              />
              {errors.email && <div className="errorMessage">{errors.email}</div>}
            </div>
            <div className="form-group">
              <label className="required-field">Password</label>
              <div className="input-div">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  name="password"
                  onChange={(e) => {
                    setErrors({});
                    setPassword(e.target.value);
                  }}
                />
                <button type="button" className="show-password-btn" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                </button>
              </div>
              {errors.password && <div className="errorMessage">{errors.password}</div>}
            </div>
            <div className="label-div">
              <label className="checkbox">
                <Checkbox
                  sx={{ pl: '0px !important' }}
                  value={rememberMe}
                  onChange={(e) => setRememberMe(e.target.value)}
                  size="small"
                  disableRipple
                />
                Remember me
              </label>
              <label className="forgot-password-btn" onClick={() => navigate('/forgot-password')}>
                Forgot password
              </label>
            </div>
            {error_message && (
              <p className="error-message" style={{ marginBottom: 15, textAlign: 'center' }}>
                {error_message}
              </p>
            )}
            <ButtonComponent onClick={signInUser} disabled={!email || !password} showLoading={disableSubmitButton}>
              Login
            </ButtonComponent>
          </form>
          <p className="login-text">
            Don’t have an account?{' '}
            <span onClick={() => (window.location.href = 'https://getsaral.com/demo')}>Book a Demo with us</span>
          </p>
        </div>
      </div>
    </div>
  );
}
